import CalendarEntryContentView from "Components/Calendar/CalendarEntryContentView";
import CalendarEntryDraggableView from "Components/Calendar/CalendarEntryDraggableView";
import {
	CALENDAR_ENTRY_HEIGHT,
	CALENDAR_ENTRY_TYPE_HEIGHT,
	CalendarEntry,
	TABLE_CELL_MARGIN_Y,
	useDraggedItem,
} from "Components/Calendar/CalendarEntryHelper";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { ResizePixelSet } from "Components/Resize/ResizeHelper";
import ResizeView, { ResizeDirection } from "Components/Resize/ResizeView";
import { MinuteRange } from "Components/WorkingHours/types/MinuteRange";

import { useIsBrowserOrIpadOs } from "Helpers/PlatformHelper";
import { ValidData } from "Store/types/ValidData";
import { Project } from "domain-entities";
import moment from "moment";
import React, { CSSProperties, FC, useState } from "react";

interface Props {
	calendarEntry: CalendarEntry;
	colorHex: string;
	projects: Project[];
	scheduleEntryTypeIndex: number;
	cellSize: CellSize;
	weekday: number;
	visibleMinutes: MinuteRange;
	resizePixelSet: ResizePixelSet;
	dropType: string;
	isFullDay?: boolean;
	isNew?: boolean;
	isSingleRow?: boolean;
	selectCalendarEntry: () => void;
	onResizeCalendarEntry: (
		calendarEntry: Partial<CalendarEntry> & ValidData
	) => Promise<void>;
	tooltip: JSX.Element | null;
}

const CalendarWeekEntryView: FC<Props> = (props) => {
	const isInternalAdmin = useIsInternalAdmin();
	const [isResizing, setIsResizing] = useState(false);
	const [resizeWidth, setResizeWidth] = useState(0);
	const [resizeLeft, setResizeLeft] = useState(0);
	const startOfWeek = useCalendar((state) => state.startOfWeek).clone();
	const endOfWeek = startOfWeek.clone().add(1, "week");
	const startTime = moment.unix(props.calendarEntry.startTime);
	const endTime = moment.unix(props.calendarEntry.endTime);
	const startTimeMinutes = startTime.hours() * 60 + startTime.minutes();
	const endTimeMinutes = endTime.hours() * 60 + endTime.minutes();
	const isStartTimeBeforeStartOfWeek = startTime.isBefore(startOfWeek);
	const isEndTimeAfterEndOfWeek = endTime.isAfter(endOfWeek);
	const cellWidth = props.cellSize.week.dayWidth;
	const width = getWidth();
	const left = getLeft();
	const isBrowserOrIpadOs = useIsBrowserOrIpadOs();
	const { isDragging, drag, preview } = useDraggedItem(
		props.dropType,
		props.calendarEntry,
		props.colorHex,
		props.isNew
	);

	function getLeft() {
		if (props.isFullDay || isStartTimeBeforeStartOfWeek) {
			return 0;
		} else {
			return Math.floor(
				(cellWidth * (startTimeMinutes - props.visibleMinutes.start)) /
					(props.visibleMinutes.end - props.visibleMinutes.start)
			);
		}
	}

	function getWidth() {
		if (props.isFullDay) {
			const width =
				cellWidth *
				Math.min(
					7,
					endTime.diff(startTime, "days"),
					endTime.diff(startOfWeek, "days"),
					endOfWeek.diff(startTime, "days")
				);

			return Math.floor(width) - 1;
		} else {
			const dayStartWidth = cellWidth - getLeft();
			const dayEndWidth =
				(cellWidth * (endTimeMinutes - props.visibleMinutes.start)) /
				(props.visibleMinutes.end - props.visibleMinutes.start);

			const width = Math.min(
				7 * cellWidth,
				(endTime.startOf("day").diff(startTime.startOf("day"), "days") - 1) *
					cellWidth +
					dayStartWidth +
					dayEndWidth,
				endTime.startOf("day").diff(startOfWeek.startOf("day"), "days") *
					cellWidth +
					dayEndWidth,
				(endOfWeek.startOf("day").diff(startTime.startOf("day"), "days") - 1) *
					cellWidth +
					dayStartWidth
			);

			return Math.floor(width) - 1;
		}
	}

	function getStyle() {
		const x = left + resizeLeft;
		const y =
			TABLE_CELL_MARGIN_Y +
			(props.scheduleEntryTypeIndex >= 0 && !props.isSingleRow
				? props.scheduleEntryTypeIndex * CALENDAR_ENTRY_TYPE_HEIGHT +
				  CALENDAR_ENTRY_HEIGHT
				: 0);

		const style: CSSProperties = {
			transform: `translate3d(${x}px, ${y}px, 0)`,
			width: width + resizeWidth,
			zIndex: isResizing ? 2 : 1,
		};

		return style;
	}

	function renderResizeView(resizeDirection: ResizeDirection) {
		if (
			isInternalAdmin &&
			isBrowserOrIpadOs &&
			((!isStartTimeBeforeStartOfWeek && resizeDirection === "left") ||
				(!isEndTimeAfterEndOfWeek && resizeDirection === "right"))
		) {
			return (
				<ResizeView
					columnWidth={props.cellSize.week.dayWidth}
					calendarEntry={props.calendarEntry}
					calendarEntryWidth={width}
					calendarEntryLeft={
						props.cellSize.week.nameWidth +
						props.cellSize.offset.left +
						props.weekday * cellWidth +
						left
					}
					resizeDirection={resizeDirection}
					resizePixels={props.resizePixelSet[resizeDirection]}
					isResizing={isResizing}
					isTooltipVisible={!props.isFullDay}
					isFullDay={props.isFullDay}
					setIsResizing={setIsResizing}
					setResizeWidth={setResizeWidth}
					setResizeLeft={setResizeLeft}
					selectScheduleEntry={props.selectCalendarEntry}
					onChange={(dateTime) =>
						props.onResizeCalendarEntry({
							id: props.calendarEntry.id,
							[resizeDirection === "left" ? "startTime" : "endTime"]: dateTime,
						})
					}
				/>
			);
		}
	}

	return (
		<CalendarEntryDraggableView
			calendarEntry={props.calendarEntry}
			colorHex={props.colorHex}
			isDragging={isDragging}
			isResizing={isResizing}
			onClick={props.selectCalendarEntry}
			preview={preview}
			style={getStyle()}
			isSingleRow={props.isSingleRow}
			isCutOff={{
				left: isStartTimeBeforeStartOfWeek,
				right: isEndTimeAfterEndOfWeek,
			}}
			tooltip={props.tooltip}
		>
			{renderResizeView("left")}
			<CalendarEntryContentView
				isDragging={isDragging}
				calendarEntry={props.calendarEntry}
				projects={props.projects}
				drag={drag}
			/>
			{renderResizeView("right")}
		</CalendarEntryDraggableView>
	);
};

export default CalendarWeekEntryView;
